import { ChangeDetectorRef, Directive, ElementRef, Inject, Optional, Self } from '@angular/core';
import { AbstractControl, FormControlName, NgModel } from '@angular/forms';
import { InputTypeDirective } from './input-type.directive';

/**
 * Abstract base class for input components, responsible for handling common input behaviors
 * like ID/Name binding, attribute manipulation, and form control integration.
 *
 * This is meant to be extended and is not a standalone directive.
 */
@Directive()
export class InputRootController implements InputTypeDirective<any> {
  /** Internal label value translated from the actual label **/
  public internalLabel?: string;

  /**
   * Constructor for `InputRootController`.
   * Injects optional Angular form directives and sets up visual styling for the input element.
   *
   * @param ngModel Optional Angular `NgModel` directive for template-driven forms.
   * @param formControl Optional Angular `FormControlName` directive for reactive forms.i
   * @param cdr Angular `ChangeDetectorRef`, useful for manual change detection.
   * @param elementRef Reference to the native HTML input, select, or textarea element.
   */
  constructor(
    @Optional() @Self() @Inject(NgModel) protected readonly ngModel: NgModel,
    @Optional()
    @Self()
    @Inject(FormControlName)
    protected readonly formControl: FormControlName,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly elementRef: ElementRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) {
    /** Add visual classes to the input element */
    this.elementRef.nativeElement.classList.add("ec-input");
  }

  /**
   * Set an HTML attribute on the underlying element.
   * Useful for dynamic attribute binding from parent components.
   *
   * @param attribute The name and value of the attribute to set.
   */
  public setAttribute(attribute: { name: string; value: string }): void {
    if (this.elementRef.nativeElement) {
      this.elementRef.nativeElement.setAttribute(
        attribute.name,
        attribute.value
      );
    }
  }

  /**
   * Get the value of a specific HTML attribute from the element.
   *
   * @param attribute The name of the attribute to retrieve.
   * @returns The attribute value, or `null`/`undefined` if not found.
   */
  public getAttribute(attribute: string): string | null | void {
    if (this.elementRef.nativeElement) {
      return this.elementRef.nativeElement.getAttribute(attribute);
    }
  }

  /**
   * Set the `id` and `name` attributes on the element.
   * This ensures the input is properly referenced by associated labels.
   *
   * @param id A unique ID string to assign.
   */
  public setId(id: string): void {
    if (this.elementRef.nativeElement) {
      this.elementRef.nativeElement.id = id;
      this.elementRef.nativeElement.name = id;
    }
  }

  /**
   * Get the associated Angular form control.
   * Falls back to an empty object if no control is available.
   *
   * @returns The associated `AbstractControl` or an empty stub.
   */
  public getFormControl(): AbstractControl {
    if (this.formControl) {
      return this.formControl.control as AbstractControl;
    }
    // Fallback for unsupported usage
    return {} as AbstractControl;
  }

  public setInternalLabel(label: string): void {
    this.internalLabel = String(label).trim();
  }
}
